import React, {
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Lets a dialog that the client arrived *for* go first, and anything we want to raise ourselves wait.
 *
 * A report mail's approve button lands on the report with the approval open, and the account may also
 * still owe us a password. Both opened at once, over each other, and the client had to deal with our
 * question before answering the one they came to answer.
 *
 * A count rather than a flag: the report already has two dialogs that can be waiting on arrival — the
 * approval and an unread answer to a dispute — and either may be on screen while the other is not.
 */
const PromptQueueContext = createContext<{ waiting: boolean; hold: () => () => void }>({
  waiting: false,
  hold: () => () => undefined,
});

export const PromptQueueProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [held, setHeld] = useState(0);

  /*
   * Nothing of ours shows until the tree below has had its first effects.
   *
   * A holder registers in an effect, and effects run child first, so on the very first commit the queue
   * still looks empty to anything that only reads it.
   */
  const [settled, setSettled] = useState(false);
  useEffect(() => setSettled(true), []);

  /*
   * And nothing shows while an instruction from a mail is still waiting to be acted on.
   *
   * Settling is not enough on its own: the dialog the instruction opens lives inside the report, which
   * mounts only once its data has loaded. Between the first effects and that arrival the queue is empty
   * and honestly so, which let the set-password prompt open, sit there, and then be covered by the
   * approval. The instruction is in the URL from the very first render, so it can be waited on before
   * anything exists to register a hold.
   *
   * Read live rather than once: `ApproveAction` takes the hold and clears the parameter in the same
   * effect pass, so the hold is in place by the time this turns false and the handover has no gap.
   * `approve` is the only such instruction today; a second one belongs in this list beside it.
   */
  const [searchParams] = useSearchParams();
  const instructionPending = searchParams.get('approve') === '1';

  const hold = useCallback(() => {
    setHeld(count => count + 1);
    let released = false;
    return () => {
      // Guarded: React runs an effect's cleanup once, but a caller holding the handle itself may not.
      if (released) return;
      released = true;
      setHeld(count => Math.max(0, count - 1));
    };
  }, []);

  const value = useMemo(
    () => ({ waiting: !settled || held > 0 || instructionPending, hold }),
    [settled, held, instructionPending, hold],
  );

  return <PromptQueueContext.Provider value={value}>{children}</PromptQueueContext.Provider>;
};

/** Hold the queue while `active`, and let go when it turns false or the holder unmounts. */
export const useHoldPrompts = (active: boolean): void => {
  const { hold } = useContext(PromptQueueContext);

  useEffect(() => {
    if (!active) return undefined;
    return hold();
  }, [active, hold]);
};

/** Whether something the client came for is still on screen, or might be about to be. */
export const usePromptsWaiting = (): boolean => useContext(PromptQueueContext).waiting;
